import React, { useState } from 'react';
import { Button } from './ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { useAuth } from '../hooks/useAuth';

interface SignUpFormProps {
  onSignUpSuccess?: () => void;
  onSwitchToLogin?: () => void;
}

const SignUpForm: React.FC<SignUpFormProps> = ({ onSignUpSuccess, onSwitchToLogin }) => {
  const [formData, setFormData] = useState({
    email: '',
    password: '',
    confirmPassword: '',
    name: ''
  });
  const [validationErrors, setValidationErrors] = useState<Record<string, string>>({});
  const { register, isLoading, error } = useAuth();

  // メール形式チェック
  const isValidEmail = (email: string): boolean => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  };

  // バリデーション関数
  const validateForm = (): boolean => {
    const errors: Record<string, string> = {};

    // メールアドレスチェック
    if (!formData.email) {
      errors.email = 'メールアドレスを入力してください';
    } else if (!isValidEmail(formData.email)) {
      errors.email = '有効なメールアドレスを入力してください';
    }

    // パスワードチェック
    if (!formData.password) {
      errors.password = 'パスワードを入力してください';
    } else if (formData.password.length < 8) {
      errors.password = 'パスワードは8文字以上で入力してください';
    }

    // パスワード確認チェック
    if (!formData.confirmPassword) {
      errors.confirmPassword = 'パスワード確認を入力してください';
    } else if (formData.password !== formData.confirmPassword) {
      errors.confirmPassword = 'パスワードが一致しません';
    }

    setValidationErrors(errors);
    return Object.keys(errors).length === 0;
  };

  // フォーム送信処理
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    try {
      const success = await register({
        email: formData.email,
        password: formData.password,
        name: formData.name || undefined
      });

      if (success) {
        // 新規登録成功をコンソールに表示
        console.log('新規登録成功');
        onSignUpSuccess?.();
        // フォームをリセット
        setFormData({
          email: '',
          password: '',
          confirmPassword: '',
          name: ''
        });
        // ページをリロードして認証状態を反映
        window.location.reload();
      }
    } catch (err) {
      // エラーハンドリングはuseAuthフックで処理される
      console.error('サインアップエラー:', err);
    }
  };

  // 入力値変更ハンドラー
  const handleInputChange = (field: keyof typeof formData) => (e: React.ChangeEvent<HTMLInputElement>) => {
    setFormData(prev => ({
      ...prev,
      [field]: e.target.value
    }));
    
    // バリデーションエラーをクリア
    if (validationErrors[field]) {
      setValidationErrors(prev => ({
        ...prev,
        [field]: ''
      }));
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4 sm:p-6">
      <Card className="w-full max-w-md">
        <CardHeader className="text-center px-4 sm:px-6">
          <CardTitle className="text-xl sm:text-2xl font-bold text-gray-900">
            サブスクナビ
          </CardTitle>
          <CardDescription className="text-base sm:text-sm">
            新しいアカウントを作成してください
          </CardDescription>
        </CardHeader>
        <CardContent className="px-4 sm:px-6">
          <form onSubmit={handleSubmit} className="space-y-4">
            {error && (
              <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-md text-sm">
                {error.message}
              </div>
            )}

            {/* 名前入力（任意） */}
            <div className="space-y-2">
              <Label htmlFor="name" className="text-base sm:text-sm">名前（任意）</Label>
              <Input
                id="name"
                type="text"
                placeholder="山田太郎"
                value={formData.name}
                onChange={handleInputChange('name')}
                disabled={isLoading}
                autoComplete="name"
              />
            </div>

            {/* メールアドレス入力 */}
            <div className="space-y-2">
              <Label htmlFor="email" className="text-base sm:text-sm">メールアドレス*</Label>
              <Input
                id="email"
                type="email"
                placeholder="your@example.com"
                value={formData.email}
                onChange={handleInputChange('email')}
                required
                disabled={isLoading}
                autoComplete="email"
                inputMode="email"
                className={validationErrors.email ? 'border-red-500' : ''}
              />
              {validationErrors.email && (
                <p className="text-red-500 text-sm">{validationErrors.email}</p>
              )}
            </div>

            {/* パスワード入力 */}
            <div className="space-y-2">
              <Label htmlFor="password" className="text-base sm:text-sm">パスワード*</Label>
              <Input
                id="password"
                type="password"
                placeholder="8文字以上のパスワード"
                value={formData.password}
                onChange={handleInputChange('password')}
                required
                disabled={isLoading}
                autoComplete="new-password"
                className={validationErrors.password ? 'border-red-500' : ''}
              />
              {validationErrors.password && (
                <p className="text-red-500 text-sm">{validationErrors.password}</p>
              )}
            </div>

            {/* パスワード確認入力 */}
            <div className="space-y-2">
              <Label htmlFor="confirmPassword" className="text-base sm:text-sm">パスワード確認*</Label>
              <Input
                id="confirmPassword"
                type="password"
                placeholder="パスワードを再入力"
                value={formData.confirmPassword}
                onChange={handleInputChange('confirmPassword')}
                required
                disabled={isLoading}
                autoComplete="new-password"
                className={validationErrors.confirmPassword ? 'border-red-500' : ''}
              />
              {validationErrors.confirmPassword && (
                <p className="text-red-500 text-sm">{validationErrors.confirmPassword}</p>
              )}
            </div>

            <Button 
              type="submit" 
              className="w-full" 
              disabled={isLoading}
            >
              {isLoading ? 'アカウント作成中...' : 'アカウント作成'}
            </Button>
          </form>

          <div className="mt-6 text-center space-y-3">
            <p className="text-sm sm:text-sm text-gray-600">
              すでにアカウントをお持ちの場合は{' '}
              <button 
                type="button"
                onClick={onSwitchToLogin}
                className="text-blue-600 hover:underline font-medium touch-manipulation"
              >
                ログイン
              </button>
            </p>
            <p className="text-sm sm:text-sm text-gray-600">
              すでにログイン済みの場合は{' '}
              <a 
                href="/dashboard"
                className="text-green-600 hover:underline font-medium"
              >
                ダッシュボードへ
              </a>
            </p>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default SignUpForm;
